import { NativeModules, Platform } from 'react-native';

type NativeVersionInfo = {
  versionCode?: number;
  versionName?: string;
  applicationId?: string;
  buildType?: string;
  debug?: boolean;
};

type TonyteAppInfoModule = {
  getVersionInfo?: () => Promise<NativeVersionInfo>;
};

const TonyteAppInfo = NativeModules.TonyteAppInfo as TonyteAppInfoModule | undefined;

export type AppVersionInfo = {
  platform: typeof Platform.OS;
  versionCode: number;
  versionName: string;
  applicationId?: string;
  buildType?: string;
  debug?: boolean;
};

// Both platforms expose TonyteAppInfo.getVersionInfo(): Android since 4.x
// (TonyteAppInfoModule.kt), iOS from the build that added
// ios/TonyteIOS/AppInfo/TonyteAppInfo.m. versionCode is the Android
// versionCode / iOS CFBundleVersion; versionName is the user-facing version.
export async function getInstalledAppVersion(): Promise<AppVersionInfo | null> {
  if (Platform.OS !== 'android' && Platform.OS !== 'ios') {
    return null;
  }

  const nativeInfo = await TonyteAppInfo?.getVersionInfo?.();
  const versionCode = Number(nativeInfo?.versionCode);
  if (!Number.isFinite(versionCode)) {
    return null;
  }

  return {
    platform: Platform.OS,
    versionCode,
    versionName: typeof nativeInfo?.versionName === 'string' ? nativeInfo.versionName : '',
    applicationId: typeof nativeInfo?.applicationId === 'string' ? nativeInfo.applicationId : undefined,
    buildType: typeof nativeInfo?.buildType === 'string' ? nativeInfo.buildType : undefined,
    debug: typeof nativeInfo?.debug === 'boolean' ? nativeInfo.debug : undefined,
  };
}

/**
 * Compares dotted versions numerically ("1.0.10" > "1.0.9"; missing parts
 * count as 0; any non-numeric suffix such as "-beta" is ignored).
 * Returns -1, 0 or 1. Mirrored in functions/appUpdates.js.
 */
export function compareVersions(a: string, b: string): number {
  const parse = (value: string) => String(value || '').trim().split(/[.+-]/).slice(0, 4).map(part => parseInt(part, 10) || 0);
  const left = parse(a);
  const right = parse(b);
  for (let index = 0; index < Math.max(left.length, right.length, 3); index += 1) {
    const diff = (left[index] || 0) - (right[index] || 0);
    if (diff) return diff > 0 ? 1 : -1;
  }
  return 0;
}

/**
 * Short, stable, Firestore-field-safe key for an FCM token: two 32-bit
 * FNV-1a lanes (different offset bases), hex. The backend recomputes it from
 * users.pushTokens to find each device's reported version
 * (users.pushDevices[key]) — mirrored in functions/appUpdates.js
 * deviceKeyForToken. Not a security hash.
 */
export function deviceKeyForToken(token: string): string {
  const lane = (seed: number) => {
    let hash = seed >>> 0;
    for (let index = 0; index < token.length; index += 1) {
      hash ^= token.charCodeAt(index);
      hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash.toString(16).padStart(8, '0');
  };
  return `d${lane(0x811c9dc5)}${lane(0x050c5d1f)}`;
}
